import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { useToast } from '../../state/toast-state';
import type { ToastItem } from '../../state/toast-state';

/**
 * Toast notification component with cva-based variant system.
 * Uses role="status" and aria-live="polite" for screen reader announcements.
 */
const toastVariants = cva(
  'pointer-events-auto relative flex w-full items-center justify-between gap-2 rounded-lg border p-4 shadow-lg transition-all',
  {
    variants: {
      variant: {
        success: 'border-success/50 bg-success/10 text-success',
        error: 'border-destructive/50 bg-destructive/10 text-destructive',
        info: 'border-primary/50 bg-primary/10 text-primary',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

// ─── Toast ───────────────────────────────────────────────────────────────────

interface ToastProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof toastVariants> {
  readonly toast: ToastItem;
  readonly onDismiss: (id: string) => void;
}

/**
 * Individual toast notification.
 * Announced to screen readers via role="status" and aria-live="polite".
 */
function Toast({ toast, onDismiss, className, ...props }: ToastProps): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(toastVariants({ variant: toast.variant }), className)}
      {...props}
    >
      <p className="text-sm font-medium">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-current opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label="Dismiss notification"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

// ─── Toaster ─────────────────────────────────────────────────────────────────

/**
 * Toaster renders the toast notification queue.
 * Place at app root level for portal-based rendering.
 */
function Toaster(): React.JSX.Element {
  const { toasts, dismissToast } = useToast();

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex max-w-sm flex-col gap-2"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={dismissToast} />
      ))}
    </div>
  );
}

export { Toast, Toaster, toastVariants };
